// 261023 E3-P3h1 (H5 / V11) — chữ cho NGƯỜI DÙNG của từng bước: một dòng mô tả dưới tên bước. THUẦN, không antd/react.
// Lấy từ mockup Rev 2.2 (`DESC`), sửa cho đúng mã đang chạy (K1): bước ③ không còn hứa «máy tự nhận từ chắc chắn» (chưa có độ chắc từng từ).
// Quy ước chữ (K8): «bước ①–⑤» = vùng việc; «Chặng 1–3» = chặng máy tự chạy.
import type { WorkbenchStageKey } from '../../../types/adaptationWorkbench';

export const STAGE_DESC: Record<WorkbenchStageKey, string> = {
  source: 'Đưa chương vào và quét lấy nguyên liệu. Bạn chỉ cần kiểm chương đủ dài; còn lại máy làm.',
  skeleton: 'Máy dựng bản chuyển thể: nhân vật, bối cảnh, cao trào, số tập. Bạn đọc rồi duyệt — đây là chỗ dừng thứ nhất.',
  localize: 'Máy đổi tên nhân vật, bối cảnh và từ vựng sang văn hoá của phim. Bạn xem lại và chấp nhận; từ chưa chấp nhận không chặn việc viết hay đẩy.',
  split: 'Máy chia truyện thành tập và chốt dàn tập. Thường bạn không phải làm gì ở đây.',
  script: 'Máy viết kịch bản từng tập. Bạn xem lướt, sửa chỗ có lỗi, rồi duyệt để đẩy sang Chapters — chỗ dừng thứ hai.',
};

/** Sổ duyệt (không phải một bước ①–⑤). */
export const REVIEW_DESC = 'Một chỗ nhìn cả truyện sau khi máy viết xong: giám khảo AI chấm từng tập, lỗi cần xem, tên gốc rò, tình tiết chưa lên phim, đã đẩy chưa. Bấm một mục để mở đúng chỗ cần xử lý.';

export const stageDesc = (key: WorkbenchStageKey): string => STAGE_DESC[key];

// ─── Hộp «Bước này chưa chạy» / «Máy đang chạy bước này» (V12, K4/K5) ──────────

/** Câu «máy sẽ tự chạy bước này ở chặng nào» — CHỈ dùng khi truyện chạy theo chuỗi «Truyện mới». */
export const STAGE_WHEN: Record<WorkbenchStageKey, string> = {
  source: 'Máy sẽ quét khi bạn bấm “Bắt đầu” ở dải phía trên (Chặng 1). Muốn quét riêng bước này thì dùng “⋯”.',
  skeleton: 'Máy tự chạy ngay sau bước ① Nguồn, trong Chặng 1: dựng khung xương (nhân vật, bối cảnh, cao trào, số tập) và tổng quan truyện, rồi dừng chờ bạn duyệt.',
  localize: 'Máy tự chạy trong Chặng 2, ngay sau khi bạn duyệt khung xương: đặt tên trong phim và đổi từ vựng theo văn hoá đích.',
  split: 'Máy tự chạy trong Chặng 2, sau bản địa hoá: chia truyện thành tập; nếu cổng AI không gắn cờ thì máy tự chốt dàn tập.',
  script: 'Máy tự chạy ở cuối Chặng 2: viết kịch bản mọi tập. Xong thì dừng chờ bạn xem lướt rồi bấm “Duyệt & đẩy”.',
};
export const NOTRUN_TITLE = 'Bước này chưa chạy';
export const RUNNING_TITLE = 'Máy đang chạy bước này…';
export const RUNNING_NOTE = 'Chạy nền, tốn AI. Bạn có thể rời trang; kết quả hiện ngay ở đây khi xong.';
export const OWN_SUMMARY = 'Tự xử lý bước này';
export const SOURCE_RUNNING_NOTE = 'Máy đang quét nguồn ở Chặng 1 — xong sẽ tự sang bước ②.';

export interface NotRunBox {
  title: string;
  text: string;
  /** Dòng nhỏ dưới (đang chạy: nói việc máy đang làm). */
  note: string | null;
  /** «Tự xử lý bước này» (gập): chỉ bước chưa chạy. `lockLine` ≠ null ⇒ bước đang khoá — hộp NÓI VÌ SAO, không mời bấm nút không bấm được. */
  own: { summary: string; lockLine: string | null; showBody: boolean } | null;
}

/** `lockText`: lý do khoá của bước («cần xong bước ① Nguồn trước.») hoặc null khi bước không khoá. `runningLabel`: «Đang chia tập». */
export const notRunBoxOf = (stage: WorkbenchStageKey, status: 'notRun' | 'running', lockText: string | null, runningLabel: string | null): NotRunBox => {
  if (status === 'running') {
    return { title: RUNNING_TITLE, text: RUNNING_NOTE, note: runningLabel, own: null };
  }
  return {
    title: NOTRUN_TITLE, text: STAGE_WHEN[stage], note: null,
    own: { summary: OWN_SUMMARY, lockLine: lockText ? `Đang khoá: ${lockText}` : null, showBody: !lockText },
  };
};

/** R4/F3 — chân bảng Sổ: không trỏ vào nút «Đẩy sang Chapters…» ở header khi nó đã ẩn (dải chuỗi đang giữ việc đẩy). */
export const reviewFootText = (headerPushShown: boolean): string =>
  `Tick các tập «Đạt» rồi bấm «Duyệt & đẩy N tập» để đẩy riêng những tập đó; ${headerPushShown ? 'hoặc dùng «Đẩy sang Chapters…» ở trên để đẩy cả truyện' : 'muốn đẩy cả truyện thì bấm «Duyệt & đẩy» ở dải phía trên'}. Các cột khác chỉ để đọc — bấm một tập để mở đúng chỗ.`;
